/**
 * SD-8.4 (FR-26, spec-fr26-module-library.md §Surface): derived module facts and input rules.
 *
 * The library stores integer millimetres and watts because datasheets publish them and the
 * canvas needs exact figures; the audience also thinks in feet-and-inches and efficiency. Those
 * are DERIVED HERE AT DISPLAY TIME, never stored — the spec's "no efficiency column" rule: a
 * stored copy eventually disagrees with the derived one.
 *
 * `validateModuleInput` mirrors the 20260805g CHECK constraints EXACTLY (watts 100–1000, each
 * dimension 500–3000 integer mm), so the form refuses precisely what the database would refuse,
 * in words instead of a 23514. The database remains the enforcer; this is the explanation.
 */

export const MODULE_LIMITS = Object.freeze({
  watts: { min: 100, max: 1000 },
  mm: { min: 500, max: 3000 },
} as const);

const MM_PER_INCH = 25.4;

/** Module efficiency in percent at STC (1000 W/m²), or null when the inputs cannot say. */
export function derivedEfficiencyPct(
  watts: number,
  width_mm: number,
  height_mm: number
): number | null {
  if (!Number.isFinite(watts) || !Number.isFinite(width_mm) || !Number.isFinite(height_mm)) {
    return null;
  }
  const areaM2 = (width_mm / 1000) * (height_mm / 1000);
  if (watts <= 0 || areaM2 <= 0) return null;
  return (watts / (areaM2 * 1000)) * 100;
}

/** '22.0%', or null when the inputs cannot say. */
export function formatEfficiency(
  watts: number,
  width_mm: number,
  height_mm: number
): string | null {
  const pct = derivedEfficiencyPct(watts, width_mm, height_mm);
  return pct === null ? null : `${pct.toFixed(1)}%`;
}

/**
 * '5 ft 8 in' — nearest inch, with the rounding carry handled: 11.9 in rounds to 12 and must
 * become the next foot, never "0 ft 12 in".
 */
export function feetInches(mm: number): string {
  const totalInches = Math.round(mm / MM_PER_INCH);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches - feet * 12;
  return `${feet} ft ${inches} in`;
}

/** Long side by short side, datasheet convention (height_mm is the long side). */
export function physicalSize(width_mm: number, height_mm: number): string {
  return `${feetInches(height_mm)} × ${feetInches(width_mm)}`;
}

export interface ModuleInput {
  manufacturer: string;
  model: string;
  watts: number;
  width_mm: number;
  height_mm: number;
}

export type ModuleInputErrors = Partial<Record<keyof ModuleInput, string>>;

const intBetween = (n: number, min: number, max: number): boolean =>
  Number.isInteger(n) && n >= min && n <= max;

/** Every failure at once, so the admin fixes the form in one pass. Empty object = valid. */
export function validateModuleInput(input: ModuleInput): ModuleInputErrors {
  const errors: ModuleInputErrors = {};
  if (!input.manufacturer.trim()) {
    errors.manufacturer = 'Manufacturer is required.';
  }
  if (!input.model.trim()) {
    errors.model = 'Model code is required — the code (like LR5-54HTH-420M), not the family name.';
  }
  if (!intBetween(input.watts, MODULE_LIMITS.watts.min, MODULE_LIMITS.watts.max)) {
    errors.watts = `Watts must be a whole number from ${MODULE_LIMITS.watts.min} to ${MODULE_LIMITS.watts.max}.`;
  }
  if (!intBetween(input.width_mm, MODULE_LIMITS.mm.min, MODULE_LIMITS.mm.max)) {
    errors.width_mm = `Width must be a whole number from ${MODULE_LIMITS.mm.min} to ${MODULE_LIMITS.mm.max} mm.`;
  }
  if (!intBetween(input.height_mm, MODULE_LIMITS.mm.min, MODULE_LIMITS.mm.max)) {
    errors.height_mm = `Height must be a whole number from ${MODULE_LIMITS.mm.min} to ${MODULE_LIMITS.mm.max} mm.`;
  }
  return errors;
}
